import { formatDateLocal } from "@/shared/lib/date-time";
import type { FormField } from "../types/form-builder";

interface FormPreviewContentProps {
  formName: string;
  fields: FormField[];
  description?: string | null;
  categoryName?: string | null;
  departmentName?: string | null;
}

export function FormPreviewContent({
  formName,
  fields,
  description,
  categoryName,
  departmentName,
}: FormPreviewContentProps) {
  const renderFieldPreview = (
    field: FormField,
    fieldId?: string,
  ): React.ReactElement => {
    const options = field.config.options;

    switch (field.type) {
      case "text":
      case "email":
      case "phone":
        return (
          <input
            id={fieldId}
            type="text"
            className="w-full bg-transparent text-sm border-b border-gray-300 py-2 print:border-gray-400"
            disabled
          />
        );
      case "textarea":
        return (
          <textarea
            id={fieldId}
            className="min-h-[80px] w-full resize-none bg-transparent text-sm border border-gray-300 rounded p-2 print:border-gray-400"
            disabled
          />
        );
      case "number":
        return (
          <input
            type="number"
            className="w-full bg-transparent text-sm border-b border-gray-300 py-2 print:border-gray-400"
            disabled
          />
        );
      case "date":
        return (
          <input
            type="date"
            className="w-full bg-transparent text-sm border-b border-gray-300 py-2 print:border-gray-400"
            disabled
          />
        );
      case "datetime":
        return (
          <input
            type="datetime-local"
            className="w-full bg-transparent text-sm border-b border-gray-300 py-2 print:border-gray-400"
            disabled
          />
        );
      case "time":
        return (
          <input
            type="time"
            className="w-full bg-transparent text-sm border-b border-gray-300 py-2 print:border-gray-400"
            disabled
          />
        );
      case "select":
        return (
          <select
            className="w-full bg-transparent text-sm border border-gray-300 rounded px-3 py-2 print:border-gray-400"
          >
            {options && options.length > 0
              ? options.map(
                  (
                    opt: { label?: string; value?: string },
                    idx: number,
                  ): React.ReactElement => {
                    const optValue = String(opt.value ?? idx);
                    const optLabel = String(opt.label ?? opt.value ?? idx);
                    return (
                      <option key={optValue} value={optValue}>
                        {optLabel}
                      </option>
                    );
                  },
                )
              : null}
          </select>
        );
      case "multiselect":
        return (
          <select
            className="w-full bg-transparent text-sm border border-gray-300 rounded px-3 py-2 print:border-gray-400"
            multiple
          >
            {options && options.length > 0
              ? options.map(
                  (
                    opt: { label?: string; value?: string },
                    idx: number,
                  ): React.ReactElement => {
                    const optValue = String(opt.value ?? idx);
                    const optLabel = String(opt.label ?? opt.value ?? idx);
                    return (
                      <option key={optValue} value={optValue}>
                        {optLabel}
                      </option>
                    );
                  },
                )
              : null}
          </select>
        );
      case "radio":
        return (
          <div className="space-y-2">
            {options && options.length > 0
              ? options.map(
                  (
                    opt: { label?: string; value?: string },
                    idx: number,
                  ): React.ReactElement => {
                    const optValue = String(opt.value ?? idx);
                    const optLabel = String(opt.label ?? opt.value ?? idx);
                    const radioId = `preview-radio-${optValue}-${idx}`;
                    return (
                      <label
                        key={optValue}
                        htmlFor={radioId}
                        className="flex items-center gap-2 text-sm"
                      >
                        <input
                          id={radioId}
                          type="radio"
                          name={`preview-${field.type}`}
                          value={optValue}
                          disabled
                        />
                        <span>{optLabel}</span>
                      </label>
                    );
                  },
                )
              : null}
          </div>
        );
      case "checkbox": {
        const checkboxId = fieldId || `preview-checkbox-${Date.now()}`;
        return (
          <label htmlFor={checkboxId} className="flex items-center gap-2 text-sm">
            <input id={checkboxId} type="checkbox" disabled />
            <span>ຕິກເພື່ອຢືນຢັນ</span>
          </label>
        );
      }
      case "file":
        return (
          <div className="rounded-md border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500 print:border-gray-400">
            <p>ກົດເພື່ອເລືອກແຟ້ມ</p>
          </div>
        );
      case "signature":
        return (
          <div className="rounded-md border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 print:border-gray-400">
            <p>ລາຍເຊັນ</p>
          </div>
        );
      default:
        return (
          <input
            type="text"
            className="w-full bg-transparent text-sm border-b border-gray-300 py-2 print:border-gray-400"
            disabled
          />
        );
    }
  };

  return (
    <div
      id="form-pdf-content"
      className="mx-auto max-w-4xl bg-white p-8 font-serif text-gray-900 leading-relaxed print:max-w-none print:p-0 print:font-serif print:text-gray-900 print:leading-relaxed"
      style={{
        fontFamily: '"Times New Roman", Times, serif',
        lineHeight: "1.5",
        color: "#1a1a1a",
      }}
    >
      {/* PDF Header */}
      <div className="mb-8 border-gray-800 border-b-4 pb-6 print:mb-6" data-tourid="preview-header">
        <div className="mb-6 text-center">
          <h1 className="font-bold text-4xl text-gray-800 uppercase tracking-wider">
            FREEDOM Sole Co.Ltd
          </h1>
          <p className="mt-2 text-gray-600 text-lg">ຟອມ</p>
          <div className="mx-auto mt-4 h-1 w-24 bg-gray-800" />
        </div>

        <div className="flex items-start justify-between text-sm">
          <div>
            {categoryName && (
              <p className="font-semibold">
                ໝວດໝູ່: <span className="font-normal">{categoryName}</span>
              </p>
            )}
            {departmentName && (
              <p className="mt-1 font-semibold">
                ພະແນກ: <span className="font-normal">{departmentName}</span>
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="font-semibold">ວັນທີພິມ:</p>
            <p className="font-normal">
              {formatDateLocal(new Date())}
            </p>
          </div>
        </div>
      </div>

      {/* Form Title */}
      <div className="mb-8 text-center" data-tourid="preview-form-title">
        <h2 className="mb-4 border-gray-300 border-b-2 pb-4 font-bold text-3xl text-gray-800 uppercase tracking-wide">
          {formName}
        </h2>
        {description && (
          <p className="mt-4 text-gray-600 text-base leading-relaxed">
            {description}
          </p>
        )}
      </div>

      {/* Form Fields */}
      {fields.length > 0 ? (
        <div className="space-y-6" data-tourid="preview-fields">
          {fields
            .sort((a, b) => a.order - b.order)
            .map((field, index) => (
              <div key={field.id || index} className="mb-6">
                <div className="mb-2 block font-semibold text-gray-800 text-base">
                  {field.label}
                  {field.required && (
                    <span className="ml-1 text-red-600">*</span>
                  )}
                </div>
                <div className="mt-2">{renderFieldPreview(field, field.id)}</div>
              </div>
            ))}
        </div>
      ) : (
        <div className="py-12 text-center text-gray-500">
          <p>ຍັງບໍ່ມີຊ່ອງຂໍ້ມູນ</p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-12 border-gray-300 border-t-2 pt-6 text-center text-sm text-gray-600">
        <p>ເອກະສານນີ້ຖືກສ້າງຈາກ Freedom ERP Admin System</p>
      </div>
    </div>
  );
}
